import axios, { AxiosInstance } from 'axios';
import type { RedeemConsentApi, RedeemConsentApiResult, RedeemRequestView } from '@kioskgaming/ui';
import { API_BASE_URL, STORAGE_KEYS } from '../constants';
import {
  getShopOnlineBalanceLedgerStub,
  getShopOnlineBalanceLedgerStubRowById,
  SHOP_ONLINE_BALANCE_LEDGER_STUB_ENABLED,
} from '../fixtures/shopOnlineBalanceLedgerStub';
import type {
  ShopCreditPurchaseData,
  ShopWalletLedgerRow,
  ShopDashboardData,
  ShopHomeData,
  ShopHomeSearchData,
  ShopLoginResponse,
  ShopPagination,
  ShopPlayer,
  ShopKioskDomainPayload,
  ShopKioskDomainRecord,
  ZeroxStaticWallet,
  ShopPendingWithdrawalsResponse,
  ShopApproveWithdrawalResponse,
  ShopRejectWithdrawalResponse,
  ShopUsdWalletLedgerResponse,
  CashierShift,
  CashierDevice,
  CashierCashDrop,
  CashierRedemptionApproval,
  CashierShiftReport,
  CashierShiftAdjustment,
  CreateShopShiftPayload,
  ReassignShopShiftPayload,
  CancelShopShiftPayload,
  CreateReplacementShiftPayload,
  ReplacementShiftResult,
  MoveShiftDevicePayload,
  CashierDevicePairingCode,
  CreateShopCashierPayload,
  ShopCashier,
  UpdateShopCashierPayload,
  ShopGameProvidersResponse,
  ShopGameWalletsResponse,
  ShopGameWalletSyncIssuesResponse,
  ShopGameWalletTransactionsResponse,
} from '../types';
import { buildShopPortalLoginBody } from './portalLoginPayload';

function asApiErrorPayload(e: unknown): { success: false; message?: string; code?: string; data?: unknown } {
  if (axios.isAxiosError(e)) {
    const payload = e.response?.data as any;
    if (payload && typeof payload === 'object') {
      const message =
        (typeof payload.message === 'string' && payload.message.trim()) ||
        (typeof payload.message_localized === 'string' && payload.message_localized.trim()) ||
        e.message;
      return {
        success: false,
        message,
        code: typeof payload.code === 'string' ? payload.code : undefined,
        data: payload.data,
      };
    }
    return { success: false, message: e.response?.status ? `Request failed (${e.response.status})` : e.message };
  }
  return { success: false, message: 'Request failed' };
}

function createClient(): AxiosInstance {
  const client = axios.create({
    baseURL: API_BASE_URL,
    timeout: 15000,
    headers: { 'Content-Type': 'application/json' },
  });

  client.interceptors.request.use((config) => {
    const token = localStorage.getItem(STORAGE_KEYS.AUTH_TOKEN);
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  });

  client.interceptors.response.use(
    (res) => res,
    (error) => {
      if (error.response?.status === 401) {
        window.dispatchEvent(new Event('auth:unauthorized'));
      }
      return Promise.reject(error);
    }
  );

  return client;
}

const client = createClient();

export async function getProviderFeeRate(params: {
  provider: string;
  operation: 'deposit' | 'withdrawal' | 'transfer';
  methodId?: string;
}): Promise<{ success: boolean; data?: { feeRate: number; matchedMethodId?: string | null }; message?: string }> {
  const { data } = await client.get('/payment/provider-fee-rate', {
    params: {
      provider: params.provider,
      operation: params.operation,
      methodId: params.methodId ?? 'all',
    },
  });
  return data;
}

export async function fetchSupportedMethods(
  provider: string,
  operation: string,
  options?: { includeDisabled?: boolean }
): Promise<{
  success?: boolean;
  data?: { methods?: Array<{ id?: string; methodId?: string; name?: string; ticker?: string; currency?: string }> };
  message?: string;
}> {
  const { data } = await client.get('/payment/supported-methods', {
    params: {
      provider,
      operation,
      ...(options?.includeDisabled ? { includeDisabled: 'true' } : {}),
    },
  });
  return data;
}

export async function fetchProviderFeeConfigs(): Promise<{
  success: boolean;
  data?: {
    items: Array<{
      id: string;
      provider: string;
      operation: string;
      methodId: string;
      paymentGateFeeRate?: number;
      systemFeeRate?: number;
      feeRate: number;
    }>;
    providers?: string[];
    /** Shop scope only: admin-enabled methods per provider::operation (one HTTP round-trip). */
    supportedCatalog?: Record<
      string,
      { methods?: Array<{ methodId?: string; id?: string; name?: string; ticker?: string; currency?: string }> }
    >;
  };
  message?: string;
}> {
  const { data } = await client.get('/shop/provider-fees');
  return data;
}

export async function upsertShopProviderFeeConfigs(payload: {
  partial?: boolean;
  items: Array<{ provider: string; operation: string; methodId?: string; isEnabled: boolean }>;
}): Promise<{
  success: boolean;
  data?: {
    items: Array<{
      id: string;
      provider: string;
      operation: string;
      methodId: string;
      paymentGateFeeRate?: number;
      systemFeeRate?: number;
      feeRate: number;
      isEnabled?: boolean;
    }>;
    providers?: string[];
  };
  message?: string;
}> {
  try {
    const { data } = await client.put('/shop/provider-fees', payload);
    return data;
  } catch (e) {
    return asApiErrorPayload(e) as {
      success: boolean;
      data?: {
        items: Array<{
          id: string;
          provider: string;
          operation: string;
          methodId: string;
          paymentGateFeeRate?: number;
          systemFeeRate?: number;
          feeRate: number;
          isEnabled?: boolean;
        }>;
        providers?: string[];
      };
      message?: string;
    };
  }
}

function createCashierClient(): AxiosInstance {
  const c = axios.create({
    baseURL: API_BASE_URL,
    timeout: 15000,
    headers: { 'Content-Type': 'application/json' },
  });

  c.interceptors.request.use((config) => {
    const token = localStorage.getItem(STORAGE_KEYS.CASHIER_AUTH_TOKEN);
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  });

  c.interceptors.response.use(
    (res) => res,
    (error) => {
      if (error.response?.status === 401) {
        window.dispatchEvent(new Event('cashier-auth:unauthorized'));
      }
      return Promise.reject(error);
    }
  );

  return c;
}

const cashierClient = createCashierClient();

export async function loginWithUsernameOrEmail(
  usernameOrEmail: string,
  password: string,
  captchaToken?: string
): Promise<ShopLoginResponse> {
  const loginId = usernameOrEmail.trim();
  const { data } = await client.post<ShopLoginResponse>('/shop-auth/login', {
    username: loginId,
    email: loginId,
    password,
    ...(captchaToken ? { captchaToken } : {}),
  });
  return data;
}

export async function verifyPortalLoginOtp(
  pendingToken: string,
  otp: string
): Promise<ShopLoginResponse> {
  const { data } = await client.post<ShopLoginResponse>('/shop-auth/verify-otp', {
    pendingToken,
    otp,
  });
  return data;
}

export async function patchPortalTwoFactor(payload: {
  two_factor_enabled: boolean;
  two_factor_channel?: 'email' | 'phone';
}): Promise<{
  success: boolean;
  message?: string;
  data?: {
    two_factor_enabled: boolean;
    two_factor_channel?: 'email' | 'phone' | null;
    email_verified?: boolean;
    phone_verified?: boolean;
  };
}> {
  const { data } = await client.patch('/shop/settings/two-factor', payload);
  return data;
}

export type PortalLoginShopOtpData = {
  role: 'shop';
  needsOtp: true;
  pendingToken: string;
  channel: 'email' | 'phone';
  maskedDestination?: string;
};

export type PortalLoginShopSuccessData = {
  role: 'shop';
  accessToken: string;
  refreshToken?: string;
  shop: Record<string, unknown>;
};

export async function portalLogin(payload: {
  loginId: string;
  password: string;
  captchaToken?: string;
}): Promise<{
  success: boolean;
  data?: PortalLoginShopOtpData | PortalLoginShopSuccessData;
  message?: string;
}> {
  const { data } = await client.post('/portal-auth/login', buildShopPortalLoginBody(payload));
  return data;
}

export type ForgotPasswordRequestResponse = {
  success: boolean;
  message?: string;
  data?: {
    token: string;
    channel: 'email' | 'phone';
    expiresInSeconds: number;
    resend_count?: number;
    next_resend_at?: string;
    expires_at?: string;
  };
};

export async function requestForgotPasswordOtp(contact: string): Promise<ForgotPasswordRequestResponse> {
  const { data } = await client.post('/shop-auth/forgot-password/request-otp', { contact });
  return data;
}

export async function verifyForgotPasswordOtp(
  token: string,
  otp: string
): Promise<{ success: boolean; message?: string; data?: { token: string } }> {
  const { data } = await client.post('/shop-auth/forgot-password/verify-otp', { token, otp });
  return data;
}

export async function confirmForgotPasswordReset(payload: {
  token: string;
  otp: string;
  newPassword: string;
  confirmPassword: string;
}): Promise<{ success: boolean; message?: string }> {
  const { data } = await client.post('/shop-auth/forgot-password/confirm', payload);
  return data;
}

export async function fetchDashboard(): Promise<{
  success: boolean;
  data?: ShopDashboardData;
}> {
  const { data } = await client.get('/shop/dashboard');
  return data;
}

export async function fetchShopHome(): Promise<{
  success: boolean;
  data?: ShopHomeData;
  message?: string;
}> {
  const { data } = await client.get('/shop/home');
  return data;
}

export async function searchShopHome(
  q: string,
  options?: { activeOnly?: boolean },
): Promise<{
  success: boolean;
  data?: ShopHomeSearchData;
  message?: string;
}> {
  const { data } = await client.get('/shop/search', {
    params: { q, activeOnly: options?.activeOnly !== false },
  });
  return data;
}

export type ShopFundingDebt = {
  outstanding_credits: number;
  threshold: number;
  frozen: boolean;
  online_deposits_blocked: boolean;
  settle_instructions?: string;
};

export async function fetchShopDebt(): Promise<{ success: boolean; data?: ShopFundingDebt }> {
  const { data } = await client.get('/shop/debt');
  return data;
}

export type DashboardV2Period = 'today' | 'week' | 'month' | '7d' | '30d' | 'custom';

export type DashboardV2PeriodParams = {
  period: DashboardV2Period;
  dateFrom?: string;
  dateTo?: string;
  tz?: string;
};

export type DashboardKpi = { value: number; previous?: number; deltaPct?: number | null };

export type ShopDashboardV2Envelope = {
  period: DashboardV2Period;
  range_start: string;
  range_end: string;
  timezone: string;
  data_as_of: string;
  comparison: { range_start: string; range_end: string };
  rollup?: { watermark_at: string | null; lag_seconds: number | null; stale: boolean };
};

export type ShopDashboardV2OverviewData = ShopDashboardV2Envelope & {
  kpis: {
    shopNetProfit: DashboardKpi;
    playerMoneyIn: DashboardKpi;
    spentTotal: DashboardKpi;
    onlineDepositsNetUsd: DashboardKpi;
    counterDepositsCashReceived: DashboardKpi;
    creditBalance: DashboardKpi;
    creditReserved: DashboardKpi;
    creditAvailable: DashboardKpi;
    usdBalanceNet: DashboardKpi;
  };
  extras: {
    roadblocksBadgeCount: number;
    cashSettlement: { expected: number; received: number; uncollected: number };
  };
  charts: {
    dailyPlayerFlow: Array<{ date: string; usdIn: number; payoutsOut: number; net: number }>;
    dailyDepositsVsRedemptions?: Array<{ date: string; deposits: number; redemptions: number }>;
  };
};

export type ShopDashboardV2FinancialData = ShopDashboardV2Envelope & {
  kpis: {
    shopNetProfit: DashboardKpi;
    playerMoneyIn: DashboardKpi;
    spentTotal: DashboardKpi;
    onlineDepositsNetUsd: DashboardKpi;
    counterDepositsCashReceived: DashboardKpi;
    creditsPurchasedUsd: DashboardKpi;
    cashPaidToAgent: DashboardKpi;
    payoutFunding: DashboardKpi;
    counterRedemptionsPaid: DashboardKpi;
    onlineWithdrawalsGrossUsd: DashboardKpi;
    onlineWithdrawalsCount: DashboardKpi;
  };
  charts: {
    dailyNetPosition: Array<{ date: string; moneyIn: number; spent: number; net: number }>;
    spentMix: Array<{ name: string; value: number }>;
    moneyInMix: Array<{ name: string; value: number }>;
  };
};

export type ShopDashboardV2PlayersData = ShopDashboardV2Envelope & {
  kpis: {
    activePlayers: DashboardKpi;
    newPlayers: DashboardKpi;
    avgDeposit: DashboardKpi;
    firstDepositConversionPct: DashboardKpi;
    playerCreditLiability: DashboardKpi;
  };
  extras: {
    topPlayers: Array<{
      userId: string;
      username: string;
      deposits: number;
      count: number;
    }>;
  };
  charts: { dailyActivePlayers: Array<{ date: string; active: number }> };
};

export type ShopDashboardV2OperationsData = ShopDashboardV2Envelope & {
  kpis: {
    openShifts: DashboardKpi;
    discrepancyShifts: DashboardKpi;
    discrepancyAmount: DashboardKpi;
    noShowShifts: DashboardKpi;
    pendingRedemptionApprovals: DashboardKpi;
    cashierTransactionsCount: DashboardKpi;
    cashierCashDelta: DashboardKpi;
    cashDropsTotal: DashboardKpi;
    cashDropsPendingCount: DashboardKpi;
  };
  extras: { cashierTxByType: Record<string, { count: number; amount: number }> };
};

export type ShopDashboardV2RoadblocksData = ShopDashboardV2Envelope & {
  roadblocks: Array<{ id: string; label: string; count: number; amountUsd: number; link: string }>;
  badgeCount: number;
};

async function fetchShopDashboardV2Tab<T>(
  tab: string,
  period: DashboardV2Period,
  refresh = false,
  extra?: Record<string, string | number>,
): Promise<T> {
  const { data } = await client.get(`/shop/dashboard/${tab}`, {
    params: { period, ...(refresh ? { refresh: 1 } : {}), ...extra },
  });
  if (!data?.success || !data.data) throw new Error(data?.message || 'Failed to load dashboard');
  return data.data as T;
}

function periodExtra(params?: { tz?: string; dateFrom?: string; dateTo?: string }) {
  const extra: Record<string, string | number> = {};
  if (params?.tz) extra.tz = params.tz;
  if (params?.dateFrom) extra.date_from = params.dateFrom;
  if (params?.dateTo) extra.date_to = params.dateTo;
  return extra;
}

export const fetchDashboardV2Overview = (
  period: DashboardV2Period,
  refresh = false,
  opts?: { tz?: string; dateFrom?: string; dateTo?: string },
) =>
  fetchShopDashboardV2Tab<ShopDashboardV2OverviewData>('overview', period, refresh, periodExtra(opts));

export const fetchDashboardV2Financial = (
  period: DashboardV2Period,
  refresh = false,
  opts?: { tz?: string; dateFrom?: string; dateTo?: string },
) =>
  fetchShopDashboardV2Tab<ShopDashboardV2FinancialData>(
    'financial',
    period,
    refresh,
    periodExtra(opts),
  );

export const fetchDashboardV2Players = (
  period: DashboardV2Period,
  refresh = false,
  opts?: { tz?: string; dateFrom?: string; dateTo?: string },
) =>
  fetchShopDashboardV2Tab<ShopDashboardV2PlayersData>('players', period, refresh, periodExtra(opts));

export const fetchDashboardV2Operations = (
  period: DashboardV2Period,
  refresh = false,
  opts?: { tz?: string; dateFrom?: string; dateTo?: string },
) =>
  fetchShopDashboardV2Tab<ShopDashboardV2OperationsData>(
    'operations',
    period,
    refresh,
    periodExtra(opts),
  );

export const fetchDashboardV2Roadblocks = (
  period: DashboardV2Period,
  refresh = false,
  opts?: { tz?: string; dateFrom?: string; dateTo?: string },
) =>
  fetchShopDashboardV2Tab<ShopDashboardV2RoadblocksData>(
    'roadblocks',
    period,
    refresh,
    periodExtra(opts),
  );

export type BalanceConversionResult = {
  credits_added: number;
  usd_deducted: number;
  cost_rate_applied: number;
  new_credit_balance: number;
  new_online_balance_net: number;
  upstream_usd_credited?: boolean;
};

export async function convertBalanceToCredits(
  amount: number,
  idempotencyKey?: string
): Promise<{ success: boolean; data?: BalanceConversionResult; message?: string }> {
  const body: { amount: number; idempotency_key?: string } = { amount };
  if (idempotencyKey && idempotencyKey.trim()) body.idempotency_key = idempotencyKey.trim();
  const { data } = await client.post('/shop/convert-balance-to-credits', body);
  return data;
}

export async function fetchShopMe(): Promise<{
  success: boolean;
  data?: { shop: Record<string, unknown> };
  message?: string;
}> {
  const { data } = await client.get('/shop/me');
  return data;
}

export async function generateShopInviteCode(): Promise<{
  success: boolean;
  data?: { inviteCode?: string; inviteLink?: string | null; created?: boolean };
  message?: string;
}> {
  const { data } = await client.post('/shop/invite-code');
  return data;
}

export type PlayerWithdrawalAutoApproveSnapshot = {
  override: number | null;
  effective: number;
  source: 'shop' | 'agent' | 'super_agent' | 'platform';
  platformDefault: number;
  chain: Array<{
    level: 'shop' | 'agent' | 'super_agent' | 'platform';
    override: number | null;
    name?: string | null;
    id?: string | null;
  }>;
};

export async function fetchShopPlayerWithdrawalAutoApprove(): Promise<{
  success: boolean;
  data?: PlayerWithdrawalAutoApproveSnapshot;
  message?: string;
}> {
  const { data } = await client.get('/shop/settings/player-withdrawal-auto-approve-threshold');
  return data;
}

export async function fetchPendingWithdrawalsForApproval(): Promise<ShopPendingWithdrawalsResponse> {
  const { data } = await client.get('/shop/withdrawals/pending');
  return data;
}

export async function fetchShopWithdrawals(params?: {
  status?: string;
  page?: number;
  limit?: number;
  sortBy?: string;
  sortOrder?: string;
}): Promise<ShopPendingWithdrawalsResponse> {
  const { data } = await client.get('/shop/withdrawals', { params });
  return data;
}

export async function approveShopWithdrawal(params: {
  transactionId: string;
  adminNotes?: string;
}): Promise<ShopApproveWithdrawalResponse> {
  const { data } = await client.post(
    `/shop/withdrawals/${encodeURIComponent(params.transactionId)}/approve`,
    { adminNotes: params.adminNotes }
  );
  return data;
}

export async function rejectShopWithdrawal(params: {
  transactionId: string;
  reason?: string;
  adminNotes?: string;
}): Promise<ShopRejectWithdrawalResponse> {
  const { data } = await client.post(
    `/shop/withdrawals/${encodeURIComponent(params.transactionId)}/reject`,
    { reason: params.reason, adminNotes: params.adminNotes }
  );
  return data;
}

export async function sendShopVerificationOtp(payload?: {
  channel?: 'email' | 'phone';
}): Promise<{
  success: boolean;
  data?: { resend_count: number; next_resend_at: string; expires_at: string; channel: 'email' | 'phone' };
  message?: string;
}> {
  const { data } = await client.post('/shop/verify/send-otp', payload ?? {});
  return data;
}

export async function confirmShopVerificationOtp(payload: {
  channel: 'email' | 'phone';
  otp: string;
}): Promise<{
  success: boolean;
  data?: { shop: Record<string, unknown>; verified: boolean; channel: 'email' | 'phone' };
  message?: string;
}> {
  const { data } = await client.post('/shop/verify/confirm', payload);
  return data;
}

export async function patchShopLowBalanceWarning(payload: {
  low_balance_warning_enabled?: boolean;
  low_balance_threshold_usd?: number;
}): Promise<{
  success: boolean;
  data?: {
    low_balance_warning_enabled: boolean;
    low_balance_threshold_usd: number;
    low_balance_effective_threshold_usd: number;
  };
  message?: string;
}> {
  const { data } = await client.patch('/shop/settings/low-balance-warning', payload);
  return data;
}

export async function purchaseShopCredits(
  amount: number,
  paymentMethod?: string
): Promise<{ success: boolean; data?: ShopCreditPurchaseData; message?: string }> {
  const body: { amount: number; payment_method?: string } = { amount };
  if (paymentMethod && paymentMethod.trim() !== '') {
    body.payment_method = paymentMethod.trim();
  }
  const { data } = await client.post('/shop/credits/purchase', body);
  return data;
}

export async function listZeroxStaticWallets(): Promise<{
  success: boolean;
  data?: { items: ZeroxStaticWallet[] };
  message?: string;
  code?: string;
}> {
  const { data } = await client.get('/shop/payments/zeroxprocessing/static-wallets');
  return data;
}

export async function initZeroxStaticWallets(currencies: string[]): Promise<{
  success: boolean;
  data?: { items: ZeroxStaticWallet[] };
  message?: string;
  code?: string;
}> {
  const { data } = await client.post('/shop/payments/zeroxprocessing/static-wallets/init', { currencies });
  return data;
}

export type ShopPlayersSortKey = 'shop_assigned_at' | 'name' | 'balance';
export type SortOrder = 'asc' | 'desc';

export async function fetchShopPlayers(params?: {
  search?: string;
  page?: number;
  limit?: number;
  sort?: ShopPlayersSortKey;
  order?: SortOrder;
  activeOnly?: boolean;
}): Promise<{
  success: boolean;
  data?: ShopPlayer[];
  pagination?: ShopPagination;
}> {
  const { data } = await client.get('/shop/players', { params });
  return data;
}

export async function createShopPlayer(payload: {
  /** Either phone or email is required */
  phone?: string;
  email?: string;
  password?: string;
  username?: string;
}): Promise<{
  success: boolean;
  data?: (ShopPlayer & { temp_password?: string; mustChangePassword?: boolean }) | { playerId?: string } | { player?: ShopPlayer };
  code?: string;
  message?: string;
}> {
  const { data } = await client.post('/shop/players', payload);
  return data;
}

export async function fetchShopPlayer(playerId: string): Promise<{
  success: boolean;
  data?: ShopPlayer;
  message?: string;
}> {
  try {
    const { data } = await client.get(`/shop/players/${encodeURIComponent(playerId)}`);
    return data;
  } catch (e: unknown) {
    const status = (e as { response?: { status?: number } }).response?.status;
    if (status === 404) {
      return { success: false, message: 'Player not found' };
    }
    throw e;
  }
}

export async function fetchShopWalletTransactions(params?: {
  page?: number;
  limit?: number;
  owner_scope?: 'self';
  period?: string;
  date_from?: string;
  date_to?: string;
  type?: string;
  search?: string;
  sort?: string;
  sort_dir?: string;
}): Promise<{
  success: boolean;
  data?: {
    items: ShopWalletLedgerRow[];
    pagination: { page: number; limit: number; total: number; totalPages: number };
    summary: {
      totalTransactions: number;
      totalDeposits: number;
      totalWithdrawals: number;
      netFlow: number;
    };
  };
  message?: string;
}> {
  const query: Record<string, string | number> = {};
  if (params?.page != null) query.page = params.page;
  if (params?.limit != null) query.limit = params.limit;
  if (params?.owner_scope) query.owner_scope = params.owner_scope;
  if (params?.period) query.period = params.period;
  if (params?.date_from) query.date_from = params.date_from;
  if (params?.date_to) query.date_to = params.date_to;
  if (params?.type) query.type = params.type;
  if (params?.search) query.search = params.search;
  if (params?.sort) query.sort = params.sort;
  if (params?.sort_dir) query.sort_dir = params.sort_dir;
  const { data } = await client.get('/shop/transactions', {
    params: Object.keys(query).length ? query : undefined,
  });
  return data;
}

async function getBlobExport(
  url: string,
  params: Record<string, string | number | undefined>,
  fallbackFilename: string,
): Promise<{ blob: Blob; filename: string }> {
  try {
    const response = await client.get<Blob>(url, {
      params,
      responseType: 'blob',
    });
    const disposition = String(response.headers['content-disposition'] || '');
    const match = /filename="([^"]+)"/i.exec(disposition);
    return { blob: response.data, filename: match?.[1] || fallbackFilename };
  } catch (e: unknown) {
    if (axios.isAxiosError(e) && e.response?.data instanceof Blob) {
      try {
        const text = await e.response.data.text();
        const parsed = JSON.parse(text) as { message?: string };
        throw new Error(parsed.message || 'Export failed');
      } catch {
        throw new Error('Export failed');
      }
    }
    throw e;
  }
}

export async function exportShopWalletTransactions(params: {
  owner_scope?: 'self';
  period?: string;
  date_from?: string;
  date_to?: string;
  type?: string;
  search?: string;
}): Promise<{ blob: Blob; filename: string }> {
  const query: Record<string, string> = {};
  if (params.owner_scope) query.owner_scope = params.owner_scope;
  if (params.period) query.period = params.period;
  if (params.date_from) query.date_from = params.date_from;
  if (params.date_to) query.date_to = params.date_to;
  if (params.type) query.type = params.type;
  if (params.search) query.search = params.search;
  return getBlobExport(
    '/shop/transactions/export',
    query,
    `credit-transactions_${params.date_from ?? 'export'}.csv`,
  );
}

export async function exportShopOnlineBalanceLedger(params: {
  currency?: string;
  type?: string;
  status?: string;
  query?: string;
  dateFrom: string;
  dateTo: string;
}): Promise<{ blob: Blob; filename: string }> {
  return getBlobExport(
    '/shop/online-balance/ledger/export',
    {
      currency: params.currency,
      type: params.type,
      status: params.status,
      query: params.query,
      dateFrom: params.dateFrom,
      dateTo: params.dateTo,
    },
    `usd-wallet-ledger_${params.dateFrom}_to_${params.dateTo}.csv`,
  );
}

export async function fetchShopCreditTransactionById(
  id: string,
): Promise<{ success: boolean; data?: { item: ShopWalletLedgerRow }; message?: string }> {
  const { data } = await client.get(`/shop/transactions/${id}`);
  return data;
}

export async function fetchShopUsdLedgerEntryById(
  id: string,
): Promise<{ success: boolean; data?: { item: Record<string, unknown> }; message?: string }> {
  if (SHOP_ONLINE_BALANCE_LEDGER_STUB_ENABLED) {
    const item = getShopOnlineBalanceLedgerStubRowById(id);
    if (!item) return { success: false, message: 'Ledger entry not found' };
    return { success: true, data: { item: item as unknown as Record<string, unknown> } };
  }
  const { data } = await client.get(`/shop/online-balance/ledger/${id}`);
  return data;
}

export async function fetchShopOnlineBalanceLedger(params?: {
  currency?: string;
  type?: string;
  page?: number;
  limit?: number;
  offset?: number;
  sortBy?: string;
  sortOrder?: string;
}): Promise<{
  success: boolean;
  data?: ShopUsdWalletLedgerResponse;
  message?: string;
}> {
  if (SHOP_ONLINE_BALANCE_LEDGER_STUB_ENABLED) {
    void params;
    await new Promise((resolve) => setTimeout(resolve, 250));
    return { success: true, data: getShopOnlineBalanceLedgerStub() };
  }
  const query: Record<string, string | number> = {};
  if (params?.currency) query.currency = params.currency;
  if (params?.type) query.type = params.type;
  const limit = params?.limit ?? 50;
  query.limit = limit;
  const page = params?.page ?? 1;
  query.offset = params?.offset ?? Math.max(0, (page - 1) * limit);
  if (params?.sortBy) {
    const sortKey = params.sortBy;
    query.sort =
      sortKey === 'createdAt'
        ? 'created'
        : sortKey === 'balanceAfter'
          ? 'balance_after'
          : sortKey;
  }
  if (params?.sortOrder) query.sort_dir = params.sortOrder.toLowerCase();
  const { data } = await client.get('/shop/online-balance/ledger', {
    params: Object.keys(query).length ? query : undefined,
  });
  return data;
}

export type OnlineDebtPayoutNetwork = 'btc' | 'eth' | 'usdt_erc' | 'usdt_trc';

export type WithdrawalOtpEligibility = {
  canWithdraw: boolean;
  emailVerified: boolean;
  phoneVerified: boolean;
  maskedEmail?: string | null;
  maskedPhone?: string | null;
};

export async function fetchShopWithdrawalOtpEligibility(): Promise<{
  success: boolean;
  data?: WithdrawalOtpEligibility;
  message?: string;
}> {
  const { data } = await client.get('/shop/online-balance/withdrawals/otp/eligibility');
  return data;
}

export async function sendShopWithdrawalOtp(body: {
  channel: 'email' | 'phone';
}): Promise<{ success: boolean; data?: { next_resend_at?: string }; message?: string }> {
  const { data } = await client.post('/shop/online-balance/withdrawals/otp/send', body);
  return data;
}

export async function verifyShopWithdrawalOtp(body: {
  channel: 'email' | 'phone';
  otp: string;
}): Promise<{ success: boolean; data?: { withdrawChallengeToken: string }; message?: string }> {
  const { data } = await client.post('/shop/online-balance/withdrawals/otp/verify', body);
  return data;
}

export async function createShopOnlineBalanceWithdrawal(body: {
  amountUsd: number;
  payoutNetwork: OnlineDebtPayoutNetwork;
  payoutWalletAddress: string;
  note?: string | null;
  idempotencyKey: string;
  withdrawChallengeToken: string;
}): Promise<{ success: boolean; data?: any; message?: string }> {
  const { data } = await client.post('/shop/online-balance/withdrawals', body);
  return data;
}

export type PortalWithdrawalLimitsSnapshot = {
  maxWithdrawalPerTransaction: number;
  maxWithdrawalPerDay: number;
  withdrawnTodayUsd: number;
  remainingTodayUsd: number;
  dailyLimitReached?: boolean;
  limitsSummary?: {
    perRequestLabel?: string;
    todayLabel?: string;
    remainingTodayLabel?: string;
  };
  wouldExceedPerTransaction?: boolean;
  wouldExceedDaily?: boolean;
};

export async function fetchShopPortalWithdrawalLimits(params?: {
  amountUsd?: number;
}): Promise<{
  success: boolean;
  data?: PortalWithdrawalLimitsSnapshot;
  message?: string;
}> {
  const { data } = await client.get('/shop/portal-withdrawal-limits', { params });
  return data;
}

export async function fetchShopOnlineBalanceWithdrawalFee(params: {
  payoutNetwork: OnlineDebtPayoutNetwork;
  amountUsd?: number;
}): Promise<{
  success: boolean;
  data?: {
    payoutNetwork: string;
    feeRate: number;
    systemFeeRate?: number;
    gatewayFeeRate?: number;
    paymentGateFeeMinUsd?: number;
    networkFeeType?: 'none' | 'percent' | 'fixed' | string;
    networkFeeRate?: number;
    networkFeeAmount?: number;
    matchedMethodId: string | null;
    feeConfigDisabled?: boolean;
    feeConfigDisabledScope?: string | null;
    feeConfigMessage?: string | null;
    netPayoutUsd?: number;
    systemFeeAmount?: number;
    gatewayFeeAmount?: number;
    tierFundingUsd?: number;
    gatewayPayoutUsd?: number;
    totalFeeUsd?: number;
    limits?: PortalWithdrawalLimitsSnapshot;
  };
  message?: string;
}> {
  const { data } = await client.get('/shop/online-balance/withdrawals/fee', { params });
  return data;
}

export async function fetchPlayerTransactions(params: {
  playerId?: string;
  phone?: string | null;
  limit?: number;
}): Promise<{
  success: boolean;
  data?: ShopWalletLedgerRow[];
}> {
  if (!params.playerId?.trim()) {
    return { success: false, data: [] };
  }
  try {
    const query: Record<string, number> = {};
    if (params.limit != null) query.limit = params.limit;
    const { data } = await client.get<{ success: boolean; data?: ShopWalletLedgerRow[] }>(
      `/shop/players/${encodeURIComponent(params.playerId)}/transactions`,
      { params: Object.keys(query).length ? query : undefined }
    );
    return data;
  } catch {
    return { success: false, data: [] };
  }
}

/** Move credits between shop wallet and a player (CDN ledger). */
export type ShopPlayerCreditsAddBody = {
  action: 'add';
  credits?: number;
  amount?: number;
  cash_received?: number;
  payment_method: string;
  received_at?: string;
  external_reference?: string;
  note?: string;
};

export type ShopPlayerCreditsDeductBody = {
  action: 'deduct';
  amount: number;
  credits?: number;
  cash_paid: number;
  received_at?: string;
  external_reference?: string;
  note?: string;
};

export type ShopPlayerCreditsResponse = {
  success: boolean;
  message?: string;
  code?: string;
  data?: {
    cash_transaction?: {
      id?: string;
      createdAt?: string;
      [key: string]: unknown;
    };
    transferResult?: unknown;
    paymentTx?: unknown;
    [key: string]: unknown;
  };
};

export async function postShopPlayerCredits(
  playerId: string,
  body: ShopPlayerCreditsAddBody | ShopPlayerCreditsDeductBody
): Promise<ShopPlayerCreditsResponse> {
  const { data } = await client.post(`/shop/players/${playerId}/credits`, body);
  return data;
}

/** Redeem that waits for the Player's approval (code or Approve in the Player app). */
export type ShopRedeemRequestBody = {
  credits: number;
  cash_paid: number;
  received_at?: string;
  external_reference?: string;
  note?: string;
};

/** The old deduct payload, returned by verify once the redeem runs. */
export type ShopRedeemResult = ShopPlayerCreditsResponse['data'];

async function toConsentResult<T>(call: Promise<{ data: { data: T } }>): Promise<RedeemConsentApiResult<T>> {
  try {
    const { data } = await call;
    return { ok: true, data: data.data };
  } catch (e) {
    const err = asApiErrorPayload(e);
    const payloadData = (err.data && typeof err.data === 'object' ? err.data : undefined) as
      | (Record<string, unknown> & { request?: RedeemRequestView | null })
      | undefined;
    return {
      ok: false,
      code: err.code,
      message: err.message || 'Request failed',
      request: payloadData?.request ?? null,
      data: payloadData,
    };
  }
}

export function createShopRedeemRequest(
  playerId: string,
  body: ShopRedeemRequestBody,
): Promise<RedeemConsentApiResult<{ request: RedeemRequestView }>> {
  return toConsentResult(client.post(`/shop/players/${playerId}/redeem-requests`, body));
}

/** API adapter for RedeemConsentStep, scoped to one Player. */
export function shopRedeemConsentApi(playerId: string): RedeemConsentApi<ShopRedeemResult> {
  const base = `/shop/players/${playerId}/redeem-requests`;
  return {
    get: (requestId) => toConsentResult(client.get(`${base}/${requestId}`)),
    verify: (requestId, code) => toConsentResult(client.post(`${base}/${requestId}/verify`, { code })),
    resend: (requestId) => toConsentResult(client.post(`${base}/${requestId}/resend`)),
    cancel: (requestId) => toConsentResult(client.post(`${base}/${requestId}/cancel`)),
  };
}

export async function getShopCashTransactions(params?: {
  page?: number;
  limit?: number;
  flow?: string;
  status?: string;
  payment_method?: string;
  date_from?: string;
  date_to?: string;
  search?: string;
  sortBy?: string;
  sortOrder?: string;
}): Promise<{
  success: boolean;
  data?: unknown;
  message?: string;
}> {
  const { data } = await client.get('/shop/cash-transactions', { params });
  return data;
}

export async function getShopCashTransactionDetail(
  id: string,
  createdAt: string,
): Promise<{ success: boolean; data?: { transaction?: unknown }; message?: string }> {
  const { data } = await client.get(`/shop/cash-transactions/${encodeURIComponent(id)}`, {
    params: { created_at: createdAt },
  });
  return data;
}

export async function exportShopCashTransactions(params?: Record<string, unknown>): Promise<{
  blob: Blob;
  filename: string;
}> {
  const response = await client.get<Blob>('/shop/cash-transactions/export', {
    params,
    responseType: 'blob',
  });
  const cd = response.headers['content-disposition'];
  const match = cd && /filename="?([^";]+)"?/.exec(cd);
  const filename = match?.[1] || 'counter-deposits-export.csv';
  return { blob: response.data, filename };
}

/** New username + temp password; server sends SMS/email like create player. */
export async function banShopPlayer(
  playerId: string,
  payload?: { reason?: string; ban_period?: string }
): Promise<{ success: boolean; data?: ShopPlayer; code?: string; message?: string }> {
  const { data } = await client.post(`/shop/players/${encodeURIComponent(playerId)}/ban`, payload ?? {});
  return data;
}

export async function unbanShopPlayer(
  playerId: string
): Promise<{ success: boolean; data?: ShopPlayer; code?: string; message?: string }> {
  const { data } = await client.post(`/shop/players/${encodeURIComponent(playerId)}/unban`);
  return data;
}

export async function closeAndReleaseShopPlayer(
  playerId: string,
  payload: { reason: string; confirm: true }
): Promise<{
  success: boolean;
  message?: string;
  code?: string;
  data?: {
    player: ShopPlayer;
    originalContacts?: Record<string, string | null>;
    releasedContacts?: Record<string, string | null>;
  };
}> {
  const { data } = await client.post(
    `/shop/players/${encodeURIComponent(playerId)}/close-and-release`,
    payload
  );
  return data;
}

export async function postShopPlayerReissueCredentials(playerId: string): Promise<{
  success: boolean;
  data?: ShopPlayer & { temp_password?: string; mustChangePassword?: boolean };
  message?: string;
}> {
  const { data } = await client.post(
    `/shop/players/${encodeURIComponent(playerId)}/credentials/reissue`,
    {}
  );
  return data;
}

export async function sendPlayerVerification(
  playerId: string,
  type: 'phone' | 'email'
): Promise<{ success: boolean; data?: { next_resend_at?: string; resend_count?: number; expires_at?: string; already_verified?: boolean }; code?: string; message?: string }> {
  const { data } = await client.post(`/shop/players/${encodeURIComponent(playerId)}/send-verification`, { type });
  return data;
}

export async function verifyPlayerVerification(
  playerId: string,
  body: { type: 'phone' | 'email'; otp: string }
): Promise<{ success: boolean; data?: { player?: ShopPlayer; verified?: boolean; type?: string; already_verified?: boolean }; code?: string; message?: string }> {
  const { data } = await client.post(`/shop/players/${encodeURIComponent(playerId)}/verify`, body);
  return data;
}

export type ShopPlayerLookupStatus = 'not_found' | 'in_shop' | 'unassigned' | 'other_shop';
export type RegistrationVerificationMethod = 'sms' | 'email';

export async function lookupPlayerByPhone(phone: string): Promise<{
  success: boolean;
  data?: { status: ShopPlayerLookupStatus; phone: string; playerId?: string };
  message?: string;
}> {
  const { data } = await client.get('/shop/players/lookup', { params: { phone } });
  return data;
}

/** Add player by phone (+ optional email) and send registration OTP. Password is server-side if needed. */
export async function registerPlayerInitiate(params: {
  phone: string;
  email?: string;
  verification_method?: RegistrationVerificationMethod;
} | string): Promise<{
  success: boolean;
  data?: {
    registration_id: string;
    phone: string;
    status: 'pending';
    expires_at: string;
    verification_method?: RegistrationVerificationMethod;
  };
  message?: string;
  code?: string;
}> {
  const payload = typeof params === 'string' ? { phone: params } : params;
  const { data } = await client.post('/shop/players/register-initiate', payload);
  return data;
}

export async function resendPlayerRegistrationOtp(
  registrationId: string,
  verificationMethod: RegistrationVerificationMethod = 'sms'
): Promise<{
  success: boolean;
  data?: {
    success: boolean;
    resend_count: number;
    next_resend_at: string;
    verification_method?: RegistrationVerificationMethod;
  };
  message?: string;
  code?: string;
}> {
  const { data } = await client.post('/shop/players/register-resend', {
    registration_id: registrationId,
    verification_method: verificationMethod
  });
  return data;
}

export async function verifyPlayerRegistration(
  registrationId: string,
  otp: string,
  verificationMethod: RegistrationVerificationMethod = 'sms'
): Promise<{ success: boolean; data?: { success: boolean; player: ShopPlayer }; message?: string; code?: string }> {
  const { data } = await client.post('/shop/players/register-verify', {
    registration_id: registrationId,
    otp,
    verification_method: verificationMethod
  });
  return data;
}

// Player transfer / shop transfer endpoints have been removed (feature deprecated).

export type ShopAdminBroadcastRow = {
  id: string;
  title: string;
  body: string;
  /** ISO 8601 from API; may be null if missing */
  createdAt: string | null;
  acknowledged: boolean;
};

export async function fetchShopBroadcasts(): Promise<{
  success: boolean;
  data?: { broadcasts: ShopAdminBroadcastRow[] };
}> {
  const { data } = await client.get('/shop/broadcasts');
  return data;
}

export async function ackShopBroadcast(broadcastId: string): Promise<{ success: boolean; message?: string }> {
  const { data } = await client.post(`/shop/broadcasts/${broadcastId}/ack`);
  return data;
}

export async function createShopBroadcast(payload: {
  title: string;
  body: string;
  targetUserIds: string[];
  expiresAt?: string | null;
}): Promise<{
  success: boolean;
  data?: { id: string; title: string; body: string; audience: string; targetIds: string[]; expiresAt: string | null; createdAt: string | null };
  message?: string;
}> {
  const { data } = await client.post('/shop/broadcasts', {
    title: payload.title,
    body: payload.body,
    target_user_ids: payload.targetUserIds,
    expires_at: payload.expiresAt ?? undefined,
  });
  return data;
}

export type MessagingChannel = 'email' | 'sms';

export type MessagingEligiblePlayer = {
  id: string;
  username: string | null;
  email: string | null;
  phone: string | null;
};

export type MessagingChannelSettings = {
  emailEnabled: boolean;
  smsEnabled: boolean;
};

export type MessagingQuote = {
  channel: MessagingChannel;
  eligibleCount: number;
  unitPriceUsd: number;
  smsSegmentsPerMessage: number | null;
  unitsPerRecipient: number;
  estimatedCostUsd: number;
  availableUsd: number;
  sufficientBalance: boolean;
  ineligibleCount: number;
};

export type MessagingCampaignSummary = {
  id: string;
  shopId: string;
  channel: MessagingChannel;
  status: string;
  subject: string | null;
  bodyPreview: string;
  body?: string;
  recipientCount: number;
  eligibleCount: number;
  sentCount: number;
  failedCount: number;
  skippedCount: number;
  quotedCostUsd: number;
  finalCostUsd: number | null;
  unitPriceUsd: number;
  smsSegmentsPerMessage: number | null;
  createdAt: string;
  completedAt: string | null;
};

export type MessagingCampaignRecipientRow = {
  id: string;
  playerId: string;
  playerUsername: string | null;
  destination: string | null;
  segmentCount: number;
  deliveryStatus: string;
  providerMessageId: string | null;
  errorMessage: string | null;
  costUsd: number;
  sentAt: string | null;
};

export type MessagingCampaignDetail = {
  campaign: MessagingCampaignSummary;
  recipients: MessagingCampaignRecipientRow[];
};

export async function fetchMessagingEligiblePlayers(channel: MessagingChannel): Promise<{
  success: boolean;
  data?: { players: MessagingEligiblePlayer[] };
}> {
  const { data } = await client.get('/shop/messaging/eligible-players', { params: { channel } });
  return data;
}

export async function fetchMessagingChannelSettings(): Promise<{
  success: boolean;
  data?: MessagingChannelSettings;
}> {
  const { data } = await client.get('/shop/messaging/settings');
  return data;
}

export async function quoteMessagingCampaign(payload: {
  channel: MessagingChannel;
  subject?: string;
  body: string;
  playerIds: string[];
}): Promise<{ success: boolean; data?: MessagingQuote }> {
  const { data } = await client.post('/shop/messaging/campaigns/quote', payload);
  return data;
}

export async function createMessagingCampaign(payload: {
  channel: MessagingChannel;
  subject?: string;
  body: string;
  playerIds: string[];
}): Promise<{ success: boolean; data?: MessagingCampaignDetail }> {
  const { data } = await client.post('/shop/messaging/campaigns', payload);
  return data;
}

export async function fetchMessagingCampaigns(): Promise<{
  success: boolean;
  data?: { campaigns: MessagingCampaignSummary[] };
}> {
  const { data } = await client.get('/shop/messaging/campaigns');
  return data;
}

export async function fetchMessagingCampaignDetail(campaignId: string): Promise<{
  success: boolean;
  data?: MessagingCampaignDetail;
}> {
  const { data } = await client.get(`/shop/messaging/campaigns/${campaignId}`);
  return data;
}

/** Contact admin (launch item A7): the Shop's tickets to the admin team. */
export type SupportTicketStatus = 'pending' | 'in_progress' | 'resolved' | 'closed';

export type SupportCategory = { id: string; name: string; description: string | null };

export type SupportTicketSummary = {
  ticketNumber: string;
  category: string;
  categoryDisplayName: string;
  status: SupportTicketStatus;
  messagePreview: string;
  createdAt: string | null;
  lastActivityAt: string | null;
  lastReplyBy: 'user' | 'admin';
  replyCount: number;
  hasUnread: boolean;
  /** 'own': the Shop opened it; 'shared': the admin team let the Shop read it (read only). */
  access: 'own' | 'shared';
};

export type SupportTicketReply = {
  id: string;
  replyType: 'user' | 'admin';
  message: string;
  adminName: string | null;
  createdAt: string | null;
  attachments: Array<{ id: string; mimeType: string; url: string }>;
};

export type SupportTicketDetail = {
  ticketNumber: string;
  category: string;
  categoryDisplayName: string;
  status: SupportTicketStatus;
  message: string;
  attachments: Array<{ id: string; mimeType: string; url: string }>;
  createdAt: string | null;
  lastActivityAt: string | null;
  resolvedAt: string | null;
  closedAt: string | null;
  closeReason: string | null;
  resolutionNote: string | null;
  estimatedResponseTime: string | null;
  canReply: boolean;
  canConfirmResolved: boolean;
  reopenUntil: string | null;
  access: 'own' | 'shared';
  replies: SupportTicketReply[];
};

export type SupportApiError = { message?: string; code?: string; errors?: Record<string, string> };

export async function fetchSupportCategories(): Promise<{ success: boolean; data?: { categories: SupportCategory[] } }> {
  const { data } = await client.get('/shop/support/categories');
  return data;
}

export async function fetchSupportTickets(params: { page?: number; limit?: number } = {}): Promise<{
  success: boolean;
  data?: { tickets: SupportTicketSummary[]; pagination: { page: number; limit: number; total: number; totalPages: number; unreadCount: number } };
}> {
  const { data } = await client.get('/shop/support/tickets', { params });
  return data;
}

export async function createSupportTicket(payload: {
  categoryId: string;
  message: string;
  attachmentIds?: string[];
}): Promise<{ success: boolean; data?: SupportTicketDetail }> {
  const { data } = await client.post('/shop/support/tickets', payload);
  return data;
}

export async function fetchSupportTicket(ticketNumber: string): Promise<{ success: boolean; data?: SupportTicketDetail }> {
  const { data } = await client.get(`/shop/support/tickets/${encodeURIComponent(ticketNumber)}`);
  return data;
}

/** One email the admin team sent this Shop (Admin › Email shops). */
export interface AdminMessageSummary {
  id: string;
  subject: string;
  preview: string;
  sentAt: string | null;
  read: boolean;
}

export interface AdminMessageDetail {
  id: string;
  subject: string;
  body: string;
  sentAt: string | null;
  read: boolean;
  readAt: string | null;
}

export async function fetchAdminMessages(params: { page?: number; limit?: number } = {}): Promise<{
  success: boolean;
  data?: { total: number; page: number; limit: number; unread: number; items: AdminMessageSummary[] };
}> {
  const { data } = await client.get('/shop/admin-emails', { params });
  return data;
}

export async function fetchAdminMessagesUnread(): Promise<{ success: boolean; data?: { unread: number } }> {
  const { data } = await client.get('/shop/admin-emails/unread-count');
  return data;
}

/** Opening a message marks it read. */
export async function fetchAdminMessage(id: string): Promise<{ success: boolean; data?: AdminMessageDetail }> {
  const { data } = await client.get(`/shop/admin-emails/${encodeURIComponent(id)}`);
  return data;
}

export async function replySupportTicket(
  ticketNumber: string,
  message: string,
  attachmentIds: string[] = []
): Promise<{ success: boolean; data?: SupportTicketDetail }> {
  const { data } = await client.post(`/shop/support/tickets/${encodeURIComponent(ticketNumber)}/replies`, { message, attachmentIds });
  return data;
}

export type SupportAttachmentLimits = { maxBytes: number; maxMegabytes: number; maxPerMessage: number; maxPerTicketPerDay: number };

export async function fetchSupportAttachmentLimits(): Promise<{ success: boolean; data?: SupportAttachmentLimits }> {
  const { data } = await client.get('/shop/support/attachment-limits');
  return data;
}

/** Stage one image (a data URL) for the next message; send its id with the message. */
export async function uploadSupportImage(image: string): Promise<{ success: boolean; data?: { id: string; mimeType: string; sizeBytes: number } }> {
  const { data } = await client.post('/shop/support/uploads', { image }, { timeout: 60000 });
  return data;
}

export async function confirmSupportTicketResolved(ticketNumber: string): Promise<{ success: boolean; data?: SupportTicketDetail }> {
  const { data } = await client.post(`/shop/support/tickets/${encodeURIComponent(ticketNumber)}/resolve`);
  return data;
}

export type ShopInAppPopupRow = {
  id: string;
  title: string;
  body: string;
  createdAt: string | null;
  displayMode?: 'once' | 'sticky_daily';
  expiresAt?: string | null;
};

export async function fetchShopInAppPopupsUnseen(): Promise<{
  success: boolean;
  data?: { popups: ShopInAppPopupRow[] };
}> {
  const { data } = await client.get('/shop/in-app-popups/unseen');
  return data;
}

export async function markShopInAppPopupSeen(popupId: string): Promise<{ success: boolean; message?: string }> {
  const { data } = await client.post(`/shop/in-app-popups/${popupId}/seen`);
  return data;
}

export async function markShopInAppPopupNeverAgain(popupId: string): Promise<{ success: boolean; message?: string }> {
  const { data } = await client.post(`/shop/in-app-popups/${popupId}/never-again`);
  return data;
}

export async function createShopInAppPopup(payload: {
  title: string;
  body: string;
  targetUserIds: string[];
  expiresAt?: string | null;
  displayMode?: 'once' | 'sticky_daily';
}): Promise<{
  success: boolean;
  data?: { id: string; title: string; body: string; audience: string; targetIds: string[]; expiresAt: string | null; createdAt: string | null; displayMode?: string };
  message?: string;
}> {
  const { data } = await client.post('/shop/in-app-popups', {
    title: payload.title,
    body: payload.body,
    target_user_ids: payload.targetUserIds,
    expires_at: payload.expiresAt ?? undefined,
    display_mode: payload.displayMode ?? 'once',
  });
  return data;
}

export async function fetchShopKioskDomain(): Promise<{
  success: boolean;
  data?: ShopKioskDomainPayload;
  message?: string;
}> {
  const { data } = await client.get('/shop/kiosk-domain');
  return data;
}

export async function createShopKioskDomain(subdomain: string): Promise<{
  success: boolean;
  data?: ShopKioskDomainRecord;
  message?: string;
}> {
  const { data } = await client.post('/shop/kiosk-domain', { subdomain });
  return data;
}

export async function setShopKioskPrimary(domainId: string): Promise<{
  success: boolean;
  data?: ShopKioskDomainPayload;
  message?: string;
}> {
  const { data } = await client.patch('/shop/kiosk-domain/primary', { domain_id: domainId });
  return data;
}

export async function patchShopKioskDomain(subdomain: string): Promise<{
  success: boolean;
  data?: ShopKioskDomainRecord;
  message?: string;
}> {
  const { data } = await client.patch('/shop/kiosk-domain', { subdomain });
  return data;
}

// -----------------------------
// Cashier API
// -----------------------------

export async function cashierLogin(payload: {
  shopId: string;
  username: string;
  password: string;
}): Promise<{ success: boolean; data?: { accessToken: string; cashier: any; shop: any }; message?: string }> {
  const { data } = await cashierClient.post('/cashier-auth/login', payload);
  return data;
}

export async function fetchCashierMe(): Promise<{ success: boolean; data?: any; message?: string }> {
  const { data } = await cashierClient.get('/cashier/me');
  return data;
}

export async function cashierOpenShift(openingCashBalance: number): Promise<{ success: boolean; data?: any; code?: string; message?: string }> {
  try {
    const { data } = await cashierClient.post('/cashier/shifts/open', { openingCashBalance });
    return data;
  } catch (e: unknown) {
    return asApiErrorPayload(e);
  }
}

export async function cashierCloseShift(closingCashCounted: number): Promise<{ success: boolean; data?: any; code?: string; message?: string }> {
  try {
    const { data } = await cashierClient.post('/cashier/shifts/close', { closingCashCounted });
    return data;
  } catch (e: unknown) {
    return asApiErrorPayload(e);
  }
}

export async function cashierFetchPlayers(params?: { search?: string; page?: number; limit?: number }): Promise<{ success: boolean; data?: any[]; pagination?: any }> {
  try {
    const { data } = await cashierClient.get('/cashier/players', { params });
    return data;
  } catch {
    return { success: false, data: [], pagination: undefined };
  }
}

export async function cashierCreatePlayer(payload: {
  /** Either phone or email is required */
  phone?: string;
  email?: string;
  username?: string;
  password?: string;
}): Promise<{ success: boolean; data?: any; code?: string; message?: string }> {
  try {
    const { data } = await cashierClient.post('/cashier/players', payload);
    return data;
  } catch (e: unknown) {
    return asApiErrorPayload(e);
  }
}

export async function cashierDepositCredits(playerId: string, payload: { amount: number; payment_method: 'cash' | 'card' | 'bank transfer' }): Promise<{ success: boolean; data?: any; code?: string; message?: string }> {
  try {
    const { data } = await cashierClient.post(`/cashier/players/${encodeURIComponent(playerId)}/deposit`, payload);
    return data;
  } catch (e: unknown) {
    return asApiErrorPayload(e);
  }
}

export async function cashierRedeemCredits(playerId: string, payload: { amount: number; cash_paid: number }): Promise<{ success: boolean; data?: any; code?: string; message?: string }> {
  try {
    const { data } = await cashierClient.post(`/cashier/players/${encodeURIComponent(playerId)}/redeem`, payload);
    return data;
  } catch (e: unknown) {
    return asApiErrorPayload(e);
  }
}

export async function fetchShopCashiers(): Promise<{ success: boolean; data?: ShopCashier[]; message?: string }> {
  const { data } = await client.get('/shop/cashiers');
  return data;
}

export async function createShopCashier(payload: CreateShopCashierPayload): Promise<{ success: boolean; data?: ShopCashier; message?: string }> {
  const { data } = await client.post('/shop/cashiers', payload);
  return data;
}

export async function suspendShopCashier(id: string): Promise<{ success: boolean; message?: string }> {
  const { data } = await client.patch(`/shop/cashiers/${encodeURIComponent(id)}/suspend`, {});
  return data;
}

export async function terminateShopCashier(id: string): Promise<{ success: boolean; message?: string }> {
  const { data } = await client.patch(`/shop/cashiers/${encodeURIComponent(id)}/terminate`, {});
  return data;
}

export async function unsuspendShopCashier(id: string): Promise<{ success: boolean; message?: string }> {
  const { data } = await client.patch(`/shop/cashiers/${encodeURIComponent(id)}/unsuspend`, {});
  return data;
}

export async function updateShopCashier(
  id: string,
  payload: UpdateShopCashierPayload
): Promise<{ success: boolean; data?: Record<string, unknown>; message?: string }> {
  const { data } = await client.patch(`/shop/cashiers/${encodeURIComponent(id)}`, payload);
  return data;
}

export async function resetShopCashierPassword(
  id: string,
  password: string
): Promise<{ success: boolean; message?: string }> {
  const { data } = await client.post(`/shop/cashiers/${encodeURIComponent(id)}/reset-password`, { password });
  return data;
}

export async function fetchShopShifts(params?: {
  from?: string;
  to?: string;
  status?: string;
}): Promise<{ success: boolean; data?: CashierShift[]; message?: string }> {
  const { data } = await client.get('/shop/shifts', { params });
  return data;
}

export async function createShopShift(
  payload: CreateShopShiftPayload
): Promise<{ success: boolean; data?: CashierShift; message?: string; code?: string }> {
  try {
    const { data } = await client.post('/shop/shifts', payload);
    return data;
  } catch (e: unknown) {
    return asApiErrorPayload(e) as { success: boolean; data?: CashierShift; message?: string; code?: string };
  }
}

export async function fetchShopShift(id: string): Promise<{ success: boolean; data?: CashierShift; message?: string }> {
  try {
    const { data } = await client.get(`/shop/shifts/${encodeURIComponent(id)}`);
    return data;
  } catch (e: unknown) {
    const status = (e as { response?: { status?: number } }).response?.status;
    if (status === 404) {
      return { success: false, message: 'Shift not found' };
    }
    throw e;
  }
}

export async function fetchCashierReport(id: string, params?: { period?: 'daily' | 'weekly' | 'monthly'; from?: string; to?: string }): Promise<{ success: boolean; data?: any; message?: string }> {
  const { data } = await client.get(`/shop/cashiers/${encodeURIComponent(id)}/report`, { params });
  return data;
}

export async function fetchCashierShifts(id: string, params?: { from?: string; to?: string }): Promise<{ success: boolean; data?: any[]; message?: string }> {
  const { data } = await client.get(`/shop/cashiers/${encodeURIComponent(id)}/shifts`, { params });
  return data;
}

export async function fetchFraudIndicators(params?: {
  period?: 'daily' | 'weekly' | 'monthly';
  from?: string;
  to?: string;
  highRedemptionThreshold?: number;
  discrepancyThreshold?: number;
}): Promise<{ success: boolean; data?: any; message?: string }> {
  const { data } = await client.get('/shop/fraud-indicators', { params });
  return data;
}

export async function fetchPlayerFraudIndicators(params?: Record<string, unknown>) {
  const { data } = await client.get('/shop/player-fraud-indicators', { params });
  return data;
}

export async function fetchPlayerFraudIndicatorDetail(id: string) {
  const { data } = await client.get(`/shop/player-fraud-indicators/${encodeURIComponent(id)}`);
  return data;
}

export async function fetchPlayerFraudWithdrawalAnalysis(transactionId: string) {
  const { data } = await client.get(
    `/shop/player-fraud-indicators/transaction/${encodeURIComponent(transactionId)}/profit-analysis`,
  );
  return data;
}

export async function resolvePlayerFraudIndicator(id: string, notes?: string) {
  const { data } = await client.post(
    `/shop/player-fraud-indicators/${encodeURIComponent(id)}/resolve`,
    { notes: notes || undefined },
  );
  return data;
}

// -----------------------------
// Cashier management (shop) — Phase 5
// -----------------------------

export async function fetchCashierDevices(): Promise<{ success: boolean; data?: CashierDevice[]; message?: string }> {
  const { data } = await client.get('/shop/cashier-devices');
  return data;
}

type CashierDeviceResult = { success: boolean; data?: CashierDevice; message?: string; code?: string };

/** Devices with an open approval request (sidebar badge). Never throws. */
export async function fetchCashierDevicesWaitingCount(): Promise<{
  success: boolean;
  data?: { count: number };
  message?: string;
  code?: string;
}> {
  try {
    const { data } = await client.get('/shop/cashier-devices/waiting-count');
    return data;
  } catch (e: unknown) {
    return asApiErrorPayload(e) as { success: boolean; data?: { count: number }; message?: string; code?: string };
  }
}

/** Approve a pending device or unblock a blocked one. `name` (1–64 chars) renames it at the same time. */
export async function approveCashierDevice(id: string, options?: { name?: string }): Promise<CashierDeviceResult> {
  try {
    const body = options?.name !== undefined ? { name: options.name } : {};
    const { data } = await client.patch(`/shop/cashier-devices/${encodeURIComponent(id)}/approve`, body);
    return data;
  } catch (e: unknown) {
    return asApiErrorPayload(e) as CashierDeviceResult;
  }
}

/** Block an active device (signs out its sessions) or reject a pending one. */
export async function blockCashierDevice(id: string): Promise<CashierDeviceResult> {
  try {
    const { data } = await client.patch(`/shop/cashier-devices/${encodeURIComponent(id)}/block`, {});
    return data;
  } catch (e: unknown) {
    return asApiErrorPayload(e) as CashierDeviceResult;
  }
}

export async function renameCashierDevice(id: string, name: string): Promise<CashierDeviceResult> {
  try {
    const { data } = await client.patch(`/shop/cashier-devices/${encodeURIComponent(id)}`, { name });
    return data;
  } catch (e: unknown) {
    return asApiErrorPayload(e) as CashierDeviceResult;
  }
}

/** One-time code (10 min) the cashier types on a new counter device to approve it. */
export async function createDevicePairingCode(options?: { name?: string }): Promise<{
  success: boolean;
  data?: CashierDevicePairingCode;
  message?: string;
  code?: string;
}> {
  try {
    const body = options?.name !== undefined ? { name: options.name } : {};
    const { data } = await client.post('/shop/cashier-devices/pairing-codes', body);
    return data;
  } catch (e: unknown) {
    return asApiErrorPayload(e) as {
      success: boolean;
      data?: CashierDevicePairingCode;
      message?: string;
      code?: string;
    };
  }
}

export async function fetchCashierExceptions(): Promise<{ success: boolean; data?: CashierShift[]; message?: string }> {
  const { data } = await client.get('/shop/cashier-exceptions');
  return data;
}

export async function fetchShiftReports(params?: {
  from?: string;
  to?: string;
}): Promise<{ success: boolean; data?: CashierShift[]; message?: string }> {
  const { data } = await client.get('/shop/shift-reports', { params });
  return data;
}

export async function fetchShiftReport(shiftId: string): Promise<{ success: boolean; data?: CashierShiftReport; message?: string }> {
  try {
    const { data } = await client.get(`/shop/shifts/${encodeURIComponent(shiftId)}/report`);
    return data;
  } catch (e: unknown) {
    return asApiErrorPayload(e) as { success: boolean; data?: CashierShiftReport; message?: string };
  }
}

export async function reconcileShift(
  shiftId: string,
  payload: { resolution?: string; managerNote?: string; finalize?: boolean }
): Promise<{ success: boolean; data?: CashierShift; message?: string }> {
  try {
    const { data } = await client.post(`/shop/shifts/${encodeURIComponent(shiftId)}/reconcile`, payload);
    return data;
  } catch (e: unknown) {
    return asApiErrorPayload(e) as { success: boolean; data?: CashierShift; message?: string };
  }
}

export async function requestShiftRecount(
  shiftId: string,
  payload?: { note?: string }
): Promise<{ success: boolean; data?: CashierShift; message?: string }> {
  try {
    const { data } = await client.post(`/shop/shifts/${encodeURIComponent(shiftId)}/recount`, payload ?? {});
    return data;
  } catch (e: unknown) {
    return asApiErrorPayload(e) as { success: boolean; data?: CashierShift; message?: string };
  }
}

export async function reassignShopShift(
  shiftId: string,
  payload: ReassignShopShiftPayload
): Promise<{ success: boolean; data?: CashierShift; message?: string; code?: string }> {
  try {
    const { data } = await client.post(`/shop/shifts/${encodeURIComponent(shiftId)}/reassign`, payload);
    return data;
  } catch (e: unknown) {
    return asApiErrorPayload(e) as { success: boolean; data?: CashierShift; message?: string; code?: string };
  }
}

export async function cancelShopShift(
  shiftId: string,
  payload: CancelShopShiftPayload
): Promise<{ success: boolean; data?: CashierShift; message?: string }> {
  try {
    const { data } = await client.post(`/shop/shifts/${encodeURIComponent(shiftId)}/cancel`, payload);
    return data;
  } catch (e: unknown) {
    return asApiErrorPayload(e) as { success: boolean; data?: CashierShift; message?: string };
  }
}

export async function createReplacementShift(
  shiftId: string,
  payload: CreateReplacementShiftPayload
): Promise<{ success: boolean; data?: ReplacementShiftResult; message?: string; code?: string }> {
  try {
    const { data } = await client.post(`/shop/shifts/${encodeURIComponent(shiftId)}/replacement`, payload);
    return data;
  } catch (e: unknown) {
    return asApiErrorPayload(e) as { success: boolean; data?: ReplacementShiftResult; message?: string; code?: string };
  }
}

/** Move an active shift to another approved, idle device (manager only; reason 3–500 chars). */
export async function moveShiftDevice(
  shiftId: string,
  payload: MoveShiftDevicePayload
): Promise<{ success: boolean; data?: CashierShift; message?: string; code?: string }> {
  try {
    const { data } = await client.post(`/shop/shifts/${encodeURIComponent(shiftId)}/move-device`, payload);
    return data;
  } catch (e: unknown) {
    return asApiErrorPayload(e) as { success: boolean; data?: CashierShift; message?: string; code?: string };
  }
}

export async function addShiftAdjustment(
  shiftId: string,
  payload: { amount: number; reason: string }
): Promise<{ success: boolean; data?: CashierShiftAdjustment; message?: string }> {
  try {
    const { data } = await client.post(`/shop/shifts/${encodeURIComponent(shiftId)}/adjustments`, payload);
    return data;
  } catch (e: unknown) {
    return asApiErrorPayload(e) as { success: boolean; data?: CashierShiftAdjustment; message?: string };
  }
}

export async function fetchCashDrops(params?: {
  status?: string;
}): Promise<{ success: boolean; data?: CashierCashDrop[]; message?: string }> {
  const { data } = await client.get('/shop/cash-drops', { params });
  return data;
}

export async function confirmCashDrop(id: string): Promise<{ success: boolean; data?: CashierCashDrop; message?: string }> {
  try {
    const { data } = await client.post(`/shop/cash-drops/${encodeURIComponent(id)}/confirm`, {});
    return data;
  } catch (e: unknown) {
    return asApiErrorPayload(e) as { success: boolean; data?: CashierCashDrop; message?: string };
  }
}

export async function rejectCashDrop(
  id: string,
  payload?: { note?: string }
): Promise<{ success: boolean; data?: CashierCashDrop; message?: string }> {
  try {
    const { data } = await client.post(`/shop/cash-drops/${encodeURIComponent(id)}/reject`, payload ?? {});
    return data;
  } catch (e: unknown) {
    return asApiErrorPayload(e) as { success: boolean; data?: CashierCashDrop; message?: string };
  }
}

export async function fetchRedemptionApprovals(): Promise<{
  success: boolean;
  data?: CashierRedemptionApproval[];
  message?: string;
}> {
  const { data } = await client.get('/shop/redemption-approvals');
  return data;
}

export async function approveRedemption(id: string): Promise<{
  success: boolean;
  data?: CashierRedemptionApproval;
  message?: string;
}> {
  try {
    const { data } = await client.post(`/shop/redemption-approvals/${encodeURIComponent(id)}/approve`, {});
    return data;
  } catch (e: unknown) {
    return asApiErrorPayload(e) as { success: boolean; data?: CashierRedemptionApproval; message?: string };
  }
}

export async function rejectRedemption(
  id: string,
  payload?: { note?: string }
): Promise<{ success: boolean; data?: CashierRedemptionApproval; message?: string }> {
  try {
    const { data } = await client.post(`/shop/redemption-approvals/${encodeURIComponent(id)}/reject`, payload ?? {});
    return data;
  } catch (e: unknown) {
    return asApiErrorPayload(e) as { success: boolean; data?: CashierRedemptionApproval; message?: string };
  }
}

// -----------------------------
// Shop Game Wallets
// -----------------------------

export async function getShopGameProviders(): Promise<{
  success: boolean;
  data?: ShopGameProvidersResponse;
  message?: string;
}> {
  const { data } = await client.get('/shop/game-wallets/providers');
  return data;
}

export async function getShopGameWallets(params: {
  providerId?: string;
  playerId?: string;
  search?: string;
  syncStatus?: string;
  balanceState?: string;
  sortBy?: string;
  sortOrder?: string;
  page?: number;
  limit?: number;
}): Promise<{ success: boolean; data?: ShopGameWalletsResponse; message?: string }> {
  const { data } = await client.get('/shop/game-wallets', { params });
  return data;
}

export async function getShopGameWalletSyncIssues(params: {
  providerId?: string;
  page?: number;
  limit?: number;
}): Promise<{ success: boolean; data?: ShopGameWalletSyncIssuesResponse; message?: string }> {
  const { data } = await client.get('/shop/game-wallets/sync-issues', { params });
  return data;
}

export async function listShopCampaignBonuses(): Promise<{ success: boolean; data?: { campaigns: import('../pages/depositBonusDisplay').CampaignBonus[] } }> {
  const { data } = await client.get('/shop/campaign-bonuses');
  return data;
}

export async function createShopCampaignBonus(body: Record<string, unknown>): Promise<{ success: boolean; data?: { campaign: import('../pages/depositBonusDisplay').CampaignBonus } }> {
  const { data } = await client.post('/shop/campaign-bonuses', body);
  return data;
}

export async function updateShopCampaignBonus(id: string, body: Record<string, unknown>): Promise<{ success: boolean; data?: { campaign: import('../pages/depositBonusDisplay').CampaignBonus } }> {
  const { data } = await client.patch(`/shop/campaign-bonuses/${id}`, body);
  return data;
}

export async function listShopCampaignBonusGrants(id: string): Promise<{ success: boolean; data?: { grants: Array<{ id: string; userId: string; walletTransactionId: string | null; usdWalletTransactionId: string | null; paymentTransactionId: number | null; username: string | null; email: string | null; depositAmount: number; bonusAmount: number; grantedCount: number; reservedCount: number }> } }> {
  const { data } = await client.get(`/shop/campaign-bonuses/${id}/grants`);
  return data;
}

export async function getShopGameWalletTransactions(params: {
  providerId: string;
  page?: number;
  limit?: number;
}): Promise<{ success: boolean; data?: ShopGameWalletTransactionsResponse; message?: string }> {
  const { data } = await client.get('/shop/game-wallets/transactions', { params });
  return data;
}
